<template>
  <table
    id="external_deal_buying_table"
    class="table"
    aria-describedby="external_deal_buying_table">
    <colgroup>
      <col style="width: 30%" />
      <col style="width: 50%" />
      <col style="width: 20%" />
    </colgroup>
    <thead>
      <tr>
        <th>{{ $t('names.ssp', { n: 1 }) }}</th>
        <th>{{ capitalize($t('names.dealID', { n: 2 })) }}</th>
        <th />
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="(sspDealIDs, i) in sspsDealIDs"
        :key="i">
        <td class="ssp-selection">
          <multiselect
            v-model="sspDealIDs.ssp"
            track-by="name"
            label="name"
            :placeholder="DASH"
            :options="ssps"
            :searchable="true"
            :show-labels="false"
            :allow-empty="false"
            :class="{ 'is-danger': $validator?.messageBag.errors.has('ssps') }"
            @select="useMediaOwners ? updateDealIDsByMediaOwner : updateDealIDsBySsp" />
        </td>
        <td class="ssp-deal-input">
          <input
            v-model="sspDealIDs.dealIDs"
            name="dealIDs"
            type="text"
            class="deal-id-input"
            :class="{ 'form-control': true, input: true, 'is-danger': $validator?.messageBag.errors.has('dealIDs') }"
            style="height: auto"
            @change="useMediaOwners ? updateDealIDsByMediaOwner : updateDealIDsBySsp" />
        </td>
        <td class="btn-remove-ssp-deal">
          <button
            v-if="canAddRows && (canBeEmpty || sspsDealIDs.length > 1)"
            class="btn btn-sm btn-default delete-ssp-deal"
            @click="removeSSPDeals(sspDealIDs)">
            {{ capitalize($t('actions.delete')) }}
          </button>
          <button
            v-else-if="!canAddRows"
            class="btn btn-sm btn-default clear-ssp-deal"
            @click="clearSSPDeal(i)">
            {{ capitalize($t('actions.clear')) }}
          </button>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td
          class="btn-add-ssp-deal"
          colspan="3">
          <button
            v-tooltip="{ content: addRowWarningMessage, visible: !canAddRows && addRowWarningMessage.length > 0 }"
            :disabled="!canAddRows && sspsDealIDs.length == 1"
            class="btn btn-sm btn-primary"
            @click="addSSPDeals">
            {{ capitalize($t('actions.add')) }}
          </button>
        </td>
      </tr>
    </tfoot>
  </table>
  <div>
    <span
      v-show="$validator?.messageBag.errors.has('ssps')"
      class="help is-danger"
      >{{ $validator?.messageBag.errors.first('ssps') }}</span
    >
    <span
      v-show="$validator?.messageBag.errors.has('dealIDs')"
      class="help is-danger"
      >{{ $validator?.messageBag.errors.first('dealIDs') }}</span
    >
  </div>
</template>

<script setup lang="ts">
  import { capitalize, inject, ref, watch } from 'vue';
  import { Multiselect } from 'vue-multiselect';

  import { LineItem, Package, Plan } from '@/common/models';
  import SSP from '@/common/models/media-owner/SSP';
  import { areArraysEqual, DASH } from '@/globals';
  import IDealsSelector from '@/org-app/components/lineItems/form/deals/types/DealsSelector';
  import EntityPropertyDealUpdateHandler from '@/org-app/services/entityPropertyHandlers/externalDeals/EntityPropertyDealUpdateHandler';
  import Validator from '@/validator/Validator';

  // region Types

  type ExternalSupplierOption = SSP | { name: string; id: number };

  interface ISingleExternalDealSelector {
    entity: LineItem | Plan | Package;
    ssps: ExternalSupplierOption[];
    useMediaOwners?: boolean;
    canBeEmpty?: boolean;
    canAddRows?: boolean;
    addRowWarningMessage?: string;
  }

  interface ISspDealIDs {
    ssp: ExternalSupplierOption | undefined | null;
    dealIDs: string;
    canAddRows?: boolean;
  }

  interface IExternalDealsSelectorExposed extends IDealsSelector {
    hasNullSpp: () => boolean;
    hasEmptyDealId: () => boolean;
  }
  // endregion

  // region Inject
  const $validator: Validator = inject('validator');
  // endregion

  // region Props
  const props = withDefaults(defineProps<ISingleExternalDealSelector>(), {
    useMediaOwners: true,
    canBeEmpty: true,
    canAddRows: true,
    addRowWarningMessage: '',
  });
  // endregion

  // region Emits
  const emit = defineEmits<{
    (e: 'sspsChanged', value: number[]): void;
    (e: 'mediaOwnersChanged', value: number[]): void;
  }>();
  // endregion

  // region Data
  const entityDealsUpdateHandler = new EntityPropertyDealUpdateHandler(props.entity);
  const sspsDealIDs = ref<ISspDealIDs[]>([
    {
      ssp: null,
      dealIDs: '',
    },
  ]);
  const lastMediaOwnerIDs = ref<string[]>();
  const lastSspIDs = ref<string[]>();

  // endregion

  // region Functions

  async function removeEntityDeals() {
    await entityDealsUpdateHandler.removeEntityDeals();
    emit('mediaOwnersChanged', null);
    emit('sspsChanged', []);
  }

  function addSSPDeals() {
    sspsDealIDs.value = [
      ...(sspsDealIDs.value ?? []),
      {
        ssp: null,
        dealIDs: '',
      },
    ];
  }

  function removeSSPDeals(sspDealIDs: ISspDealIDs) {
    sspsDealIDs.value = sspsDealIDs.value?.filter((x) => x !== sspDealIDs);
  }

  function clearSSPDeal(index: number) {
    // Clear the given row so that it can be re-used (alternative would be to remove the row and then call addSSPDeals)
    sspsDealIDs.value[index] = {
      ssp: null,
      dealIDs: '',
    };
  }

  function updateDealIDsByMediaOwner() {
    if (sspsDealIDs.value && sspsDealIDs.value.length > 0) {
      const dealIDsByMediaOwnerID: Record<number, string[]> = sspsDealIDs.value
        .filter((x) => x.ssp != null)
        .reduce((mem, x) => {
          const mediaOwnerID = `${x.ssp?.mediaOwners[0]?.id}`;
          if (mem[mediaOwnerID] == null) {
            mem[mediaOwnerID] = x.dealIDs.split(',').map((id) => id.trim());
          } else {
            mem[mediaOwnerID].push(...x.dealIDs.split(',').map((id) => id.trim()));

            // Remove duplicates dealIDs

            mem[mediaOwnerID] = [...new Set(mem[mediaOwnerID])];
          }
          return mem;
        }, {});
      const dealIDs = [
        ...new Set(
          sspsDealIDs.value
            .filter((x) => x.ssp != null && x.dealIDs.trim())
            .map((x) => x.dealIDs.split(','))
            .flat()
            .map((x) => x.trim()),
        ),
      ];

      const originalMediaOwners = sspsDealIDs.value
        .filter((x) => x.ssp != null && x.dealIDs.trim())
        .map((x) => x.ssp?.mediaOwners)
        .flat();

      // Remove duplicates mediaOwners object
      const mediaOwners = [...new Map(originalMediaOwners.map((item) => [item?.id, item])).values()];
      entityDealsUpdateHandler.updateEntityDealsByMediaOwner({
        dealIDsByMediaOwnerID,
        dealIDs,
      });

      if (
        lastMediaOwnerIDs.value == null ||
        !areArraysEqual(
          lastMediaOwnerIDs.value,
          mediaOwners.map((x) => x?.id),
        )
      ) {
        const mediaOwnersIDs = mediaOwners.map((x) => x?.id);
        emit('mediaOwnersChanged', mediaOwnersIDs);
      }
      lastMediaOwnerIDs.value = mediaOwners.map((x) => x?.id);
    } else {
      // Remove from list
      removeEntityDeals();
    }
  }

  function updateDealIDsBySsp() {
    if (sspsDealIDs.value && sspsDealIDs.value.length > 0) {
      const dealIDsBySspID: Record<number, string[]> = sspsDealIDs.value
        .filter((x) => x.ssp != null)
        .reduce((mem, x) => {
          const sspID = x.ssp.id;
          if (mem[sspID] == null) {
            mem[sspID] = x.dealIDs.split(',').map((id) => id.trim());
          } else {
            mem[sspID].push(...x.dealIDs.split(',').map((id) => id.trim()));

            // Remove duplicates dealIDs

            mem[sspID] = [...new Set(mem[sspID])];
          }
          return mem;
        }, {});
      const dealIDs = [
        ...new Set(
          sspsDealIDs.value
            .filter((x) => x.ssp != null && x.dealIDs.trim())
            .map((x) => x.dealIDs.split(','))
            .flat()
            .map((x) => x.trim()),
        ),
      ];

      const originalSsps = sspsDealIDs.value
        .filter((x) => x.ssp != null && x.dealIDs.trim())
        .map((x) => x.ssp)
        .flat();

      // Remove duplicates ssps object
      const ssps = [...new Map(originalSsps.map((item) => [item?.id, item])).values()];
      entityDealsUpdateHandler.updateEntityDealsBySSP({
        dealIDsBySspID,
        dealIDs,
      });

      if (
        lastSspIDs.value == null ||
        !areArraysEqual(
          lastSspIDs.value,
          ssps.map((x) => x?.id),
        )
      ) {
        const sspsIDs = ssps.map((x) => x?.id);
        emit('sspsChanged', sspsIDs);
      }
      lastSspIDs.value = ssps.map((x) => x?.id);
    } else {
      // Remove from list
      removeEntityDeals();
    }
  }

  function hasNullSpp(): boolean {
    return sspsDealIDs.value?.some((x) => x.ssp == null);
  }

  function hasEmptyDealId(): boolean {
    return sspsDealIDs.value?.some((x) => !x.dealIDs?.trim());
  }
  // endregion

  // region Exposed Values
  defineExpose<IExternalDealsSelectorExposed>({
    hasNullSpp,
    hasEmptyDealId,
    removeEntityDeals,
  });
  // endregion

  // region Watchers
  watch(
    sspsDealIDs,
    () => {
      if (props.useMediaOwners) {
        updateDealIDsByMediaOwner();
      } else {
        updateDealIDsBySsp();
      }
    },
    { deep: true },
  );

  // Moved from onMounted to watch in case the SSPs havent fin
  watch(
    () => props.ssps,
    () => {
      if (props.entity.dealIDsByMediaOwnerProperty != null) {
        // Restore pre-saved SSP / Deal IDs
        const json = props.entity.dealIDsByMediaOwnerProperty.value;
        if (json != null) {
          const dictionary: Record<string, string[]> = JSON.parse(json);
          sspsDealIDs.value = Object.entries(dictionary).map((x) => ({
            ssp: props.ssps.find((ssp) => ssp.mediaOwners.some((mo) => mo.id == x[0])),
            dealIDs: x[1].join(', '),
          }));
        }
      } else if (props.entity.dealIDsBySspProperty != null) {
        // Restore pre-saved SSP / Deal IDs
        const json = props.entity.dealIDsBySspProperty.value;
        if (json != null) {
          const dictionary: Record<string, string[]> = JSON.parse(json);
          sspsDealIDs.value = Object.entries(dictionary).map((x) => ({
            ssp: props.ssps.find((ssp) => ssp.id == Number(x[0])),
            dealIDs: x[1].join(', '),
          }));
        }
      }
    },
    { immediate: true },
  );
  // endregion
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;
  .ssp-deal-input {
    width: 100%;
  }
</style>
